import qs from 'query-string'

import type {
  ExtensionOption,
  PhoneNumberOption,
  PlaylistOption,
  SystemSettings,
  SystemSettingsUpdatePayload,
  VirtualFaxOption,
} from '../types'
import { SYSTEM_SETTINGS_ENDPOINTS } from './endpoints'
import type {
  ApiError,
  PaginatedApiResult,
  SystemSettingsApi,
  SystemSettingsApiClient,
} from './systemSettingsApi.types'

export const createSystemSettingsApi = (
  client: SystemSettingsApiClient,
): SystemSettingsApi => ({
  async getSystemSettings(pbxId: number): Promise<SystemSettings> {
    const response = await client.get<SystemSettings>(
      SYSTEM_SETTINGS_ENDPOINTS.SYSTEM_SETTINGS(pbxId),
    )
    if (!response.ok) {
      throw new Error(
        (response.data as ApiError | undefined)?.message ??
          `Failed to fetch system settings (${response.status ?? 'unknown'})`,
      )
    }
    return response.data as SystemSettings
  },

  async updateSystemSettings(
    pbxId: number,
    payload: SystemSettingsUpdatePayload,
  ): Promise<SystemSettings> {
    const response = await client.patch<SystemSettings>(
      SYSTEM_SETTINGS_ENDPOINTS.SYSTEM_SETTINGS(pbxId),
      payload,
    )
    if (!response.ok) {
      throw new Error(
        (response.data as ApiError | undefined)?.message ??
          `Failed to update system settings (${response.status ?? 'unknown'})`,
      )
    }
    return response.data as SystemSettings
  },

  async getPhoneNumbers(pbxId: number): Promise<PhoneNumberOption[]> {
    const allResults: PhoneNumberRaw[] = []
    let offset = 0
    const limit = 250

    while (true) {
      const queryString = qs.stringify(
        { pbx_id: pbxId, limit, offset },
        { skipNull: true, skipEmptyString: true },
      )
      const url = `${SYSTEM_SETTINGS_ENDPOINTS.PHONE_NUMBERS}?${queryString}`
      const response = await client.get<PaginatedApiResult<PhoneNumberRaw>>(url)

      if (!response.ok) {
        throw new Error(
          (response.data as ApiError | undefined)?.message ??
            `Failed to fetch phone numbers (${response.status ?? 'unknown'})`,
        )
      }

      allResults.push(...response.data!.results)
      if (!response.data!.next) break
      offset += limit
    }

    return allResults.map((p) => ({
      id: p.id,
      name: p.name,
      number: p.number,
    }))
  },

  async getExtensions(pbxId: number): Promise<ExtensionOption[]> {
    const queryString = qs.stringify(
      { pbx_id: pbxId, limit: 500 },
      { skipNull: true, skipEmptyString: true },
    )
    const url = `${SYSTEM_SETTINGS_ENDPOINTS.EXTENSIONS}?${queryString}`
    const response = await client.get<PaginatedApiResult<ExtensionRaw>>(url)

    if (!response.ok) {
      throw new Error(
        (response.data as ApiError | undefined)?.message ??
          `Failed to fetch extensions (${response.status ?? 'unknown'})`,
      )
    }

    return response.data!.results.map((e) => ({
      id: e.id,
      name: e.name,
      number: e.number,
      uuid: e.uuid,
    }))
  },

  async getVirtualFaxMachines(pbxId: number): Promise<VirtualFaxOption[]> {
    const url = SYSTEM_SETTINGS_ENDPOINTS.VIRTUAL_FAX_MACHINES(pbxId)
    const response = await client.get<
      VirtualFaxRaw[] | PaginatedApiResult<VirtualFaxRaw>
    >(url)

    if (!response.ok) {
      throw new Error(
        (response.data as ApiError | undefined)?.message ??
          `Failed to fetch virtual fax machines (${response.status ?? 'unknown'})`,
      )
    }

    const items = Array.isArray(response.data)
      ? response.data
      : response.data!.results

    return items.map((v) => ({
      id: v.id,
      name: v.name,
      number: v.number,
    }))
  },

  async getPlaylists(pbxId: number): Promise<PlaylistOption[]> {
    const url = SYSTEM_SETTINGS_ENDPOINTS.PLAYLISTS(pbxId)
    const response = await client.get<
      PlaylistRaw[] | PaginatedApiResult<PlaylistRaw>
    >(url)

    if (!response.ok) {
      if (response.status === 404) return []
      throw new Error(
        (response.data as ApiError | undefined)?.message ??
          `Failed to fetch playlists (${response.status ?? 'unknown'})`,
      )
    }

    const items = Array.isArray(response.data)
      ? response.data
      : response.data!.results

    return items.map((p) => ({
      id: p.id,
      name: p.name,
      type: p.type === 'stream' ? ('stream' as const) : ('list' as const),
      track_count: p.track_count,
      url: p.url,
    }))
  },
})

interface PhoneNumberRaw {
  id: number
  name: string
  number: string
  sms_activated_at: string | null
  type?: string
}

interface ExtensionRaw {
  id: number
  uuid: string
  name: string
  number: string
  type: string
}

interface VirtualFaxRaw {
  id: number
  name: string
  number: string
}

interface PlaylistRaw {
  id: number
  name: string
  type: string
  active: boolean
  track_count?: number
  url?: string | null
}
